import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Star, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { stats, services, testimonials } from '../mockData';
import { useNavigate } from "react-router-dom";
import clinic from "../assets/skin_treatment.webp";


const Home = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    initial: { opacity: 0 },
    whileInView: { opacity: 1 },
    viewport: { once: true },
    transition: { staggerChildren: 0.2 }
  };

  const extractNumberAndSuffix = (value) => {
  const number = parseInt(value.replace(/\D/g, ''));
  const suffix = value.replace(/[0-9]/g, '');
  return { number, suffix };
};

const AnimatedStat = ({ value }) => {
  const { number, suffix } = extractNumberAndSuffix(value);

  return (
    <motion.span
      initial={{ count: 0 }}
      whileInView={{ count: number }}
      viewport={{ once: true }}
      transition={{ duration: 2, ease: "easeOut" }}
      onUpdate={(latest) => {
        if (latest.count !== undefined) {
          const el = document.getElementById(`stat-${number}-${suffix}`);
          if (el) el.textContent = Math.floor(latest.count) + suffix;
        }
      }}
    >
      <span id={`stat-${number}-${suffix}`}>0{suffix}</span>
    </motion.span>
  );
};

const isMobile = () => {
  return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
};

const BookAppointmentButton = () => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    if (isMobile()) {
      window.location.href = "tel:+919876543210"; // 👉 replace with your phone number
    } else {
      navigate("/contact");
    }
  };

  return (
    <button
      onClick={handleClick}
      className="bg-white text-cyan-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 hover:scale-105 hover:shadow-2xl flex items-center space-x-2"
    >
      <span>Book Appointment</span>
      <ArrowRight className="w-5 h-5" />
    </button>
  );
};


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-600 via-sky-500 to-blue-600 opacity-90" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.h1
                className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Transform Your Skin,
                <span className="block bg-gradient-to-r from-white to-cyan-100 bg-clip-text text-transparent">
                  Transform Your Life
                </span>
              </motion.h1>
              <motion.p
                className="text-xl text-white/90 mb-8 leading-relaxed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                Experience world-class dermatological care with cutting-edge treatments and personalized solutions for healthier, radiant skin.
              </motion.p>
              <motion.div
                className="flex flex-wrap gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <BookAppointmentButton />
                <Link
                  to="/services"
                  className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-cyan-600 transition-all duration-300 hover:scale-105"
                >
                  View Services
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={clinic}
                  alt="Modern Clinic"
                  className="w-full h-[320px] sm:h-[400px] md:h-[500px] object-cover"

                />
                <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/50 to-transparent" />
              </div>
              <motion.div
                className="
                          absolute 
                          bottom-4 left-4 
                          md:-bottom-8 md:-left-8
                          bg-white 
                          p-4 md:p-6 
                          rounded-2xl 
                          shadow-2xl
                          max-w-[85%]
                        "

                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 1 }}
              >
                <div className="flex items-center space-x-3">
                  <Award className="w-12 h-12 text-cyan-600" />
                  <div>
                    <p className="text-2xl md:text-3xl font-bold text-gray-900">
                      5000+
                    </p>
                    <p className="text-gray-600 text-xs md:text-sm">
                      Happy Patients
                    </p>

                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <motion.section className="py-20 bg-white relative" {...fadeInUp}>
  <div className="container mx-auto px-4">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
      {stats.map((stat, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          className="text-center p-6 rounded-2xl hover:bg-gradient-to-br hover:from-cyan-50 hover:to-blue-50 transition-all duration-300 hover:scale-105"
        >
          <motion.p
            className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent mb-2"
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
          >
            <AnimatedStat value={stat.value} />
          </motion.p>

          <p className="text-gray-600 font-medium">{stat.label}</p>
        </motion.div>
      ))}
    </div>
  </div>
</motion.section>


      {/* Featured Services */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Signature Treatments</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Advanced dermatological procedures using cutting-edge technology
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            {services.slice(0, 6).map((service, index) => (
              <motion.div
                key={service.id}
                variants={fadeInUp}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/80 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <span className="bg-cyan-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {service.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-cyan-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="text-center mt-12"
            {...fadeInUp}
          >
            <Link
              to="/services"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <span>View All Services</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Choose V Derma</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Excellence in dermatological care with a patient-first approach
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Expert Care',
                description: '20+ years of specialized experience in dermatology and cosmetology',
                icon: Award
              },
              {
                title: 'Advanced Technology',
                description: 'State-of-the-art equipment and cutting-edge treatment methods',
                icon: CheckCircle
              },
              {
                title: 'Proven Results',
                description: '98% success rate with over 5000 satisfied patients',
                icon: Star
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="bg-gradient-to-br from-cyan-50 to-blue-50 p-8 rounded-2xl hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <item.icon className="w-12 h-12 text-cyan-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <motion.div className="text-center mb-16" {...fadeInUp}>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Patient{" "}
                <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                  Success Stories
                </span>
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Real results from real patients
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed text-sm italic">
                    "{testimonial.text}"
                  </p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-cyan-600 text-sm">{testimonial.treatment}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* View More Button */}
            <div className="mt-12 flex justify-center">
              <a
                href="https://share.google/LWD30vg4GiGdUXmCj"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-semibold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                View More Reviews
              </a>
            </div>
          </div>
        </section>


      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600 via-sky-500 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            {...fadeInUp}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Skin?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Book your consultation today and take the first step towards healthier, more radiant skin
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-white text-cyan-600 px-10 py-5 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 hover:scale-105 hover:shadow-2xl"
            >
              <span>Book Your Appointment</span>
              <ArrowRight className="w-6 h-6" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;